import React from 'react';
import DatePicker from 'react-datepicker';
import { TextBox, Button as TextBoxButton } from 'devextreme-react/text-box';
import { format, parseISO } from 'date-fns';
import 'react-datepicker/dist/react-datepicker.css';
import ru from 'date-fns/locale/ru';
import en from 'date-fns/locale/en-AU';
import { on } from 'devextreme/events';
import styles from './daterangepicker.module.scss';
import { getLocale } from '../../utils/functions';

// const formatDate = (date, Format) => (date
// ? format(parse(String(date), 'yyyyMMdd', new Date()), Format || 'dd.MM.yyyy')
// : null);
// const formatDate = (date, Format) => (date
//   ? format(parseISO(new Date(date)), Format || 'dd.MM.yyyy', { awareOfUnicodeTokens: true })
//   : null);

// eslint-disable-next-line max-len
// const formatDate = (date, Format) => (date ? format(new Date(date), Format || 'dd.MM.yyyy') : '');

const formatDate = (date, Format) => {
  if (!date) return '';

  let dateObj;
  if (typeof date === 'string') {
    if (date.includes('T')) {
      dateObj = parseISO(date);
    } else {
      dateObj = new Date(date.replace(/[-.]/g, '-'));
    }
  } else if (date instanceof Date) {
    dateObj = date;
  } else {
    return '';
  }

  return format(dateObj, Format || 'dd.MM.yyyy');
};

const formatToValidDate = (date) => {
  if (!date) return null;

  if (typeof date === 'string') {
    if (date.includes('T')) {
      return parseISO(date);
    }

    return new Date(date.replace(/[-.]/g, '-'));
  }

  return date instanceof Date ? date : null;
};

const DateRangePicker = ({
  onSelectEnd,
  tFormat,
  tStartDate,
  tEndDate,
  tText,
  isSingleMode = false
}) => {
  const datepicker = React.useRef(null);
  const [startDate, setStartDate] = React.useState(tStartDate ? formatDate(new Date(tStartDate), tFormat) : '');
  const [endDate, setEndDate] = React.useState(tEndDate ? formatDate(new Date(tEndDate), tFormat) : '');
  const [minDate, setMinDate] = React.useState('');
  const [isOpen, setIsOpen] = React.useState(false);
  const [selectStart, setSelectStart] = React.useState(true);
  const clickOutside = React.useCallback(() => setIsOpen(false), []);

  const memoInput = () => (
    <DateRangeInput startDate={startDate}
                    endDate={endDate}
                    isOpen={isOpen}
                    changeIsOpen={setIsOpen}
    />
  );

  const singleMemoInput = () => (
    <DateRangeInput
      startDate={startDate}
      isOpen={isOpen}
      changeIsOpen={setIsOpen}
    />
  );

  const DateRangeInput = React.forwardRef(({ onClick, changeIsOpen }, ref) => {
    const onClickInput = () => {
      changeIsOpen(true);
      onClick();
    };
    const clearButtonOption = {
      // icon: startDate && endDate ? '/images/delete.svg' : '/images/calendar.svg',
      stylingMode: 'text',
      onClick: () => {
        if (isSingleMode) {
          if (startDate) {
            setStartDate(null);
            datepicker.current.setOpen(false);
            setIsOpen(false);
            onSelectEnd('', '');
          } else {
            onClickInput();
          }
        }
        if (!isSingleMode) {
          if (startDate && endDate) {
            setStartDate(null);
            setEndDate(null);
            setMinDate(null);
            datepicker.current.setOpen(false);
            setIsOpen(false);
            onSelectEnd(null, null);
          } else {
            onClickInput();
          }
        }
      }
    };
    const onContentReady = ({ element }) => {
      on(element, 'click', onClickInput);
    };
    return (
      <div className={`${styles.inputWrapper} ${isOpen ? 'active' : ''}`}>
        <TextBox value={isSingleMode ? `${formatDate(startDate)}` : startDate ? `${formatDate(startDate)} - ${formatDate(endDate)}` : null}
                 elementAttr={{ class: isSingleMode
                   ? (startDate ? 'date-range-picker-close' : 'date-range-picker-edit')
                   : (startDate && endDate ? 'date-range-picker-close' : 'date-range-picker-edit')
                 }}
        // onClick={onClickInput}
                 onContentReady={onContentReady}
                 placeholder={tText}
                 showClearButton={false}
                 ref={ref}
        >
          <TextBoxButton name={'buttonClear'} location={'after'} options={clearButtonOption} />
        </TextBox>
      </div>
    );
  });

  const memoChange = React.useCallback(
    (date) => {
      if (selectStart) {
        setStartDate(formatDate(date, tFormat));
        setMinDate(formatDate(date, tFormat));
        setEndDate(null);
      } else {
        setEndDate(formatDate(date, tFormat));
        setMinDate(null);
        datepicker.current.setOpen(false);
        setIsOpen(false);
        onSelectEnd(formatDate(startDate, tFormat), formatDate(date, tFormat));
      }
      setSelectStart(!selectStart);
    },
    [onSelectEnd, selectStart, startDate, tFormat]
  );

  const memoChangeSingleMode = React.useCallback(
    (date) => {
      if (selectStart) {
        setStartDate(formatDate(date, tFormat));
        datepicker.current.setOpen(false);
        setIsOpen(false);
        onSelectEnd(formatDate(date, tFormat), '');
      }
      setSelectStart(!selectStart);
    },
    [onSelectEnd, selectStart, tFormat]
  );

  return (
    <div className={styles.pickerWrapper}>
      {isSingleMode ? (
        <DatePicker customInput={singleMemoInput()}
                    ref={datepicker}
                    startDate={startDate ? formatToValidDate(startDate) : null}
                    endDate={endDate ? formatToValidDate(endDate) : null}
                    showPopperArrow={false}
                    highlightDates={[new Date()]}
                    disabledKeyboardNavigation
                    locale={(getLocale() === 'ru' ? ru : en)}
                    selectsEnd
                    onChange={memoChangeSingleMode}
                    onClickOutside={clickOutside}
        />
      ) : (
        <DatePicker customInput={memoInput()}
                    ref={datepicker}
                    startDate={startDate ? formatToValidDate(startDate) : null}
                    endDate={endDate ? formatToValidDate(endDate) : null}
                    minDate={minDate ? formatToValidDate(minDate) : null}
                    showPopperArrow={false}
                    shouldCloseOnSelect={false}
                    disabledKeyboardNavigation
                    locale={(getLocale() === 'ru' ? ru : en)}
                    selectsEnd
                    onChange={memoChange}
                    onClickOutside={clickOutside}
        />
      )}
    </div>
  );
};

export default React.memo(DateRangePicker);
